import { useState, useRef, useEffect } from 'react';

const SEGMENTS = [
  { label: '0x', multiplier: 0, color: '#3b3b5c', textColor: '#ffffff99', weight: 45 },
  { label: '2x', multiplier: 2, color: '#1a3a5c', textColor: '#9fe0ff', weight: 40 },
  { label: '5x', multiplier: 5, color: '#1a3a2c', textColor: '#9af5c6', weight: 10 },
  { label: '0x', multiplier: 0, color: '#3b3b5c', textColor: '#ffffff99', weight: 45 },
  { label: '2x', multiplier: 2, color: '#1a3a5c', textColor: '#9fe0ff', weight: 40 },
  { label: '10x', multiplier: 10, color: '#3a1a0a', textColor: '#ffc9a3', weight: 5 },
  { label: '0x', multiplier: 0, color: '#3b3b5c', textColor: '#ffffff99', weight: 45 },
  { label: '2x', multiplier: 2, color: '#1a3a5c', textColor: '#9fe0ff', weight: 40 },
  { label: '5x', multiplier: 5, color: '#1a3a2c', textColor: '#9af5c6', weight: 10 },
  { label: '2x', multiplier: 2, color: '#1a3a5c', textColor: '#9fe0ff', weight: 40 },
  { label: '0x', multiplier: 0, color: '#3b3b5c', textColor: '#ffffff99', weight: 45 },
  { label: '10x', multiplier: 10, color: '#3a1a0a', textColor: '#ffc9a3', weight: 5 },
];

const TOTAL_WEIGHT = SEGMENTS.reduce((sum, s) => sum + s.weight, 0);

function pickWeightedIndex() {
  let r = Math.random() * TOTAL_WEIGHT;
  for (let i = 0; i < SEGMENTS.length; i++) {
    r -= SEGMENTS[i].weight;
    if (r <= 0) return i;
  }
  return SEGMENTS.length - 1;
}

const NUM_SEGMENTS = SEGMENTS.length;
const SEGMENT_ANGLE = 360 / NUM_SEGMENTS;
const RADIUS = 120;
const CENTER = 140;
const SVG_SIZE = CENTER * 2;

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function segmentPath(index) {
  const startAngle = index * SEGMENT_ANGLE;
  const endAngle = startAngle + SEGMENT_ANGLE;
  const start = polarToCartesian(CENTER, CENTER, RADIUS, startAngle);
  const end = polarToCartesian(CENTER, CENTER, RADIUS, endAngle);
  const largeArc = SEGMENT_ANGLE > 180 ? 1 : 0;
  return `M ${CENTER} ${CENTER} L ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

function segmentLabelPos(index) {
  const midAngle = index * SEGMENT_ANGLE + SEGMENT_ANGLE / 2;
  return polarToCartesian(CENTER, CENTER, RADIUS * 0.68, midAngle);
}

export default function SpinWheel({ coinReward = 0, onResult, disabled = false }) {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const spin = () => {
    if (spinning || disabled || result) return;

    const winIndex = pickWeightedIndex();
    const segmentCenter = winIndex * SEGMENT_ANGLE + SEGMENT_ANGLE / 2;
    const targetOffset = 360 - segmentCenter;
    const totalRotation = rotation + 1800 + targetOffset + (Math.random() * 10 - 5);

    setSpinning(true);
    setRotation(totalRotation);

    timeoutRef.current = window.setTimeout(() => {
      setSpinning(false);
      setResult(SEGMENTS[winIndex]);
      onResult?.(SEGMENTS[winIndex].multiplier);
    }, 3200);
  };

  const multiplierColor = result
    ? result.multiplier === 0 ? 'text-muted'
      : result.multiplier === 2 ? 'text-sky-300'
        : result.multiplier === 5 ? 'text-emerald-300'
          : 'text-red-300'
    : '';

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative" style={{ width: SVG_SIZE, height: SVG_SIZE, filter: 'drop-shadow(0 14px 26px rgba(0,0,0,0.45))' }}>
        {/* Rotating wheel face */}
        <svg
          width={SVG_SIZE}
          height={SVG_SIZE}
          viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? 'transform 3.2s cubic-bezier(0.17,0.67,0.12,1)' : 'none',
            display: 'block',
          }}
        >
          <defs>
            {/* radial darkening toward the rim → gives the disc curvature */}
            <radialGradient id="wheelShade" cx="50%" cy="50%" r="50%">
              <stop offset="55%" stopColor="#000" stopOpacity="0" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.42" />
            </radialGradient>
          </defs>

          {SEGMENTS.map((seg, i) => (
            <path key={`seg-${i}`} d={segmentPath(i)} fill={seg.color} stroke="#00000055" strokeWidth="1" />
          ))}

          {/* curvature shade over the segments */}
          <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="url(#wheelShade)" />

          {SEGMENTS.map((seg, i) => {
            const pos = segmentLabelPos(i);
            return (
              <text
                key={`lbl-${i}`}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={seg.label === '10x' ? '14' : '15'}
                fontWeight="900"
                fontFamily="'Syne', sans-serif"
                fill={seg.textColor}
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
                transform={`rotate(${i * SEGMENT_ANGLE + SEGMENT_ANGLE / 2}, ${pos.x}, ${pos.y})`}
              >
                {seg.label}
              </text>
            );
          })}
        </svg>

        {/* Fixed glossy dome + bevel ring + hub + pointer (do NOT rotate) */}
        <svg
          width={SVG_SIZE}
          height={SVG_SIZE}
          viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        >
          <defs>
            <linearGradient id="ringGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFE9A8" />
              <stop offset="45%" stopColor="#E8A33D" />
              <stop offset="55%" stopColor="#B8860B" />
              <stop offset="100%" stopColor="#7A5800" />
            </linearGradient>
            <radialGradient id="glassGloss" cx="38%" cy="26%" r="48%">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.4" />
              <stop offset="55%" stopColor="#fff" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="hubGrad" cx="38%" cy="32%" r="68%">
              <stop offset="0%" stopColor="#FFF1C2" />
              <stop offset="55%" stopColor="#E8A33D" />
              <stop offset="100%" stopColor="#9A6A0B" />
            </radialGradient>
          </defs>

          {/* beveled metallic outer ring */}
          <circle cx={CENTER} cy={CENTER} r={RADIUS + 4} fill="none" stroke="url(#ringGrad)" strokeWidth="9" />
          <circle cx={CENTER} cy={CENTER} r={RADIUS + 9} fill="none" stroke="#000" strokeOpacity="0.35" strokeWidth="2" />
          <circle cx={CENTER} cy={CENTER} r={RADIUS - 1} fill="none" stroke="#fff" strokeOpacity="0.18" strokeWidth="1.5" />

          {/* glass gloss highlight */}
          <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="url(#glassGloss)" />

          {/* center hub (raised knob) */}
          <circle cx={CENTER} cy={CENTER} r="26" fill="#00000055" />
          <circle cx={CENTER} cy={CENTER} r="23" fill="url(#hubGrad)" stroke="#FFE9A8" strokeWidth="2" />
          <circle cx={CENTER} cy={CENTER} r="15" fill="#241a05" />
          <ellipse cx={CENTER - 6} cy={CENTER - 8} rx="7" ry="4" fill="#fff" opacity="0.25" transform={`rotate(-25 ${CENTER - 6} ${CENTER - 8})`} />
          <text x={CENTER} y={CENTER + 1} textAnchor="middle" dominantBaseline="middle" fontSize="15" fontWeight="900" fill="#FFD740" fontFamily="'Syne', sans-serif">$</text>
        </svg>

        {/* Pointer (fixed, top) */}
        <div className="absolute left-1/2 top-0 z-10 -translate-x-1/2" style={{ filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.5))' }}>
          <svg width="26" height="32" viewBox="0 0 26 32">
            <defs>
              <linearGradient id="ptrGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFE9A8" />
                <stop offset="100%" stopColor="#E8A33D" />
              </linearGradient>
            </defs>
            <polygon points="13,30 24,5 2,5" fill="url(#ptrGrad)" stroke="#7A5800" strokeWidth="2" strokeLinejoin="round" />
            <polygon points="13,24 18,8 8,8" fill="#fff" opacity="0.25" />
          </svg>
        </div>
      </div>

      {result ? (
        <div className="w-full rounded-2xl border border-edge bg-surfaceAlt px-4 py-3 text-center">
          {result.multiplier === 0 ? (
            <p className="text-sm font-black text-muted">Sem sorte desta vez...</p>
          ) : (
            <>
              <p className={`text-2xl font-black ${multiplierColor}`}>{result.label}</p>
              <p className="mt-1 text-xs font-bold text-muted">
                {coinReward} × {result.multiplier} = <span className={`font-black ${multiplierColor}`}>{coinReward * result.multiplier} moedas</span>
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="w-full rounded-2xl border border-accent/20 bg-accent/10 px-4 py-2 text-center text-xs font-bold text-accent">
          {spinning ? 'A girar...' : 'Gira a roleta e multiplica as tuas moedas!'}
        </div>
      )}

      {!result && (
        <button
          type="button"
          onClick={spin}
          disabled={spinning || disabled}
          className="w-full rounded-2xl bg-accent py-4 text-sm font-black uppercase tracking-wider text-white shadow-[0_4px_18px_rgba(232,83,29,0.5)] transition-all active:scale-95 disabled:opacity-50"
        >
          {spinning ? 'A girar...' : 'Girar!'}
        </button>
      )}
    </div>
  );
}
